import React, { useState, useEffect, useCallback } from "react";
import { fetchWithAuth } from "../../utils/api";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

/**
 * TrialUsageBadge — A gorgeous floating badge shown to trial users
 * displaying how many free assessments remain.
 *
 * Props:
 *   - user: the authenticated user object (must have instituteId)
 */
export default function TrialUsageBadge({ user }) {
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const fetchUsageData = useCallback(async () => {
    if (!user?.instituteId) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get-usage/${user.instituteId}`,
        { credentials: "include" }
      );
      if (response && response.ok) {
        const data = await response.json();
        setUsageData(data);
      }
    } catch (err) {
      console.error("Failed to fetch trial usage:", err);
    } finally {
      setLoading(false);
    }
  }, [user?.instituteId]);

  useEffect(() => {
    fetchUsageData();
  }, [fetchUsageData]);

  if (loading || !usageData?.combinedGrading) return null;

  const { usageSize = 0, purchasedSize = 0 } = usageData.combinedGrading;
  const remaining = Math.max(0, purchasedSize - usageSize);
  const percentage = purchasedSize > 0 ? (usageSize / purchasedSize) * 100 : 0;
  const isLow = remaining <= 2 && remaining > 0;
  const isExhausted = remaining === 0;

  // Determine the status color palette
  const palette = isExhausted
    ? { primary: "#ef4444", bg: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)", text: "#991b1b", ring: "#ef4444", border: "rgba(239,68,68,0.19)", borderHover: "rgba(239,68,68,0.38)", strokeBg: "rgba(239,68,68,0.09)", labelColor: "rgba(153,27,27,0.6)" }
    : isLow
    ? { primary: "#f59e0b", bg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)", text: "#92400e", ring: "#f59e0b", border: "rgba(245,158,11,0.19)", borderHover: "rgba(245,158,11,0.38)", strokeBg: "rgba(245,158,11,0.09)", labelColor: "rgba(146,64,14,0.6)" }
    : { primary: "#10b981", bg: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", text: "#065f46", ring: "#10b981", border: "rgba(16,185,129,0.19)", borderHover: "rgba(16,185,129,0.38)", strokeBg: "rgba(16,185,129,0.09)", labelColor: "rgba(6,95,70,0.6)" };

  const statusClass = isExhausted ? "trial-status-exhausted" : isLow ? "trial-status-low" : "trial-status-normal";

  // CSS custom properties for dynamic color theming
  const cssVars = {
    "--trial-primary": palette.primary,
    "--trial-bg": palette.bg,
    "--trial-text": palette.text,
    "--trial-ring": palette.ring,
    "--trial-border": palette.border,
    "--trial-border-hover": palette.borderHover,
    "--trial-stroke-bg": palette.strokeBg,
    "--trial-label-color": palette.labelColor,
  };

  // SVG progress ring
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (Math.min(percentage, 100) / 100) * circumference;

  return (
    <div
      className={`trial-usage-badge ${statusClass}${isCollapsed ? " collapsed" : ""}`}
      onClick={() => setIsCollapsed((prev) => !prev)}
      title={isCollapsed ? "Click to expand" : "Click to collapse"}
      style={cssVars}
    >
      {/* Progress Ring */}
      <div className="trial-ring-wrapper">
        <svg className="trial-ring-svg" viewBox="0 0 44 44">
          <circle className="trial-ring-bg" cx="22" cy="22" r={radius} />
          <circle
            className="trial-ring-fill"
            cx="22"
            cy="22"
            r={radius}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
          />
        </svg>
        <div className={`trial-ring-count${isLow || isExhausted ? " trial-count-bounce" : ""}`}>
          {remaining}
        </div>
      </div>

      {/* Text Info */}
      <div className="trial-badge-text">
        <div className="trial-badge-label">Free Trial</div>
        {isExhausted ? (
          <>
            <div className="trial-badge-value">No assessments left</div>
            <a className="trial-exhausted-cta" href="https://grademe-ai.com?contact" target="_blank" rel="noopener noreferrer">
              <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: "0.6rem", marginRight: "0.2rem" }}></i>
              Upgrade now
            </a>
          </>
        ) : (
          <div className="trial-badge-value">
            <span>{remaining}</span> free assessment{remaining !== 1 ? "s" : ""} left
          </div>
        )}
      </div>
    </div>
  );
}
